import {
  Box,
  Container,
  Divider,
  Flex,
  Heading,
  Text,
  VStack,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { COMPANIES, focusTags, type Entry } from "../assets/CV";
import LogoMark from "./LogoMark";
import SectionHeader from "./SectionHeader";
import TagRow from "./TagRow";

const MotionBox = motion(Box);

// Returning to the same company reads as one tenure, not two unrelated rows, so
// consecutive entries sharing a name collapse into a single logo lockup with a
// stint timeline beneath it.
function groupByCompany(entries: Entry[]) {
  return entries.reduce<{ company: string; stints: Entry[] }[]>((groups, exp) => {
    const current = groups[groups.length - 1];
    if (current && current.company === exp.company) {
      current.stints.push(exp);
    } else {
      groups.push({ company: exp.company, stints: [exp] });
    }
    return groups;
  }, []);
}

// Deliberately minimal: logo lockup, role, dates and a single overview line.
// Detailed bullets live in the Involvement timeline instead.
export default function CompanyExperience() {
  const groups = groupByCompany(COMPANIES);

  return (
    <Box id="experience" py={{ base: 20, md: 28 }}>
      <Container maxW="5xl" px={{ base: 6, md: 8 }}>
        <SectionHeader eyebrow="02 / Industry" title="Experience" />

        <VStack align="stretch" spacing={0}>
          {groups.map((group, index) => {
            const [lead] = group.stints;
            const isMultiStint = group.stints.length > 1;

            return (
              <MotionBox
                key={`${group.company}-${lead.startDate}`}
                role="group"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: index * 0.08 }}
              >
                <Box py={{ base: 8, md: 10 }}>
                  <LogoMark
                    logo={lead.logo}
                    tone={lead.logoTone}
                    monogram={lead.monogram}
                    alt={group.company}
                    height="26px"
                  />

                  {/* With several stints the company anchors the group and each
                      role sits on the timeline; a lone stint keeps the role up
                      top so the section still leads with what he did. */}
                  {isMultiStint && (
                    <Heading
                      fontSize={{ base: "lg", md: "xl" }}
                      fontWeight="500"
                      letterSpacing="-0.02em"
                      color="white"
                      mt={5}
                    >
                      {group.company}
                    </Heading>
                  )}

                  <VStack align="stretch" spacing={0} mt={isMultiStint ? 6 : 5}>
                    {group.stints.map((exp, stintIndex) => {
                      const isLast = stintIndex === group.stints.length - 1;

                      return (
                        <Flex
                          key={`${exp.role}-${exp.startDate}`}
                          align="flex-start"
                          gap={{ base: 4, md: 6 }}
                        >
                          {isMultiStint && (
                            <Flex
                              direction="column"
                              align="center"
                              minW="9px"
                              pt={2}
                            >
                              <Box
                                w="9px"
                                h="9px"
                                borderRadius="full"
                                border="1px solid"
                                borderColor="whiteAlpha.700"
                                bg="black"
                                flexShrink={0}
                              />
                              {!isLast && (
                                <Box
                                  w="1px"
                                  flex={1}
                                  bg="whiteAlpha.200"
                                  minH="40px"
                                />
                              )}
                            </Flex>
                          )}

                          <Box flex={1} pb={isMultiStint && !isLast ? 8 : 0}>
                            <Flex
                              direction={{ base: "column", md: "row" }}
                              justify="space-between"
                              align={{ base: "flex-start", md: "baseline" }}
                              gap={{ base: 1, md: 6 }}
                            >
                              <VStack align="flex-start" spacing={1}>
                                <Heading
                                  fontSize={
                                    isMultiStint
                                      ? { base: "md", md: "lg" }
                                      : { base: "lg", md: "xl" }
                                  }
                                  fontWeight="500"
                                  letterSpacing="-0.02em"
                                  color="white"
                                >
                                  {exp.role}
                                </Heading>
                                {!isMultiStint && (
                                  <Text color="whiteAlpha.600" fontSize="sm">
                                    {exp.company}
                                  </Text>
                                )}
                              </VStack>

                              <Text
                                fontFamily="mono"
                                fontSize="xs"
                                letterSpacing="0.08em"
                                color="whiteAlpha.500"
                                whiteSpace={{ base: "normal", md: "nowrap" }}
                                flexShrink={0}
                              >
                                {exp.startDate} — {exp.endDate}
                              </Text>
                            </Flex>

                            <Text
                              color="whiteAlpha.700"
                              fontSize="sm"
                              lineHeight="1.8"
                              maxW="3xl"
                              mt={4}
                            >
                              {exp.summary ?? exp.description[0]}
                            </Text>

                            <VStack align="flex-start" spacing={5} mt={5}>
                              <TagRow label="Focus" items={focusTags(exp)} />
                              <TagRow
                                label="Technical Skills"
                                variant="solid"
                                items={exp.skills ?? []}
                              />
                            </VStack>
                          </Box>
                        </Flex>
                      );
                    })}
                  </VStack>
                </Box>

                {index !== groups.length - 1 && (
                  <Divider borderColor="whiteAlpha.100" />
                )}
              </MotionBox>
            );
          })}
        </VStack>
      </Container>
    </Box>
  );
}
